"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

/**
 * List of available theme types.
 */
export const themeList = ["light", "dark", "system"] as const;
export type ThemeType = (typeof themeList)[number];

/**
 * Interface for the Theme Context.
 */
export interface ThemeContextType {
  /** Current theme mode. */
  theme: ThemeType;
  /** Function to change the theme mode. */
  changeTheme: (newTheme: ThemeType) => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined
);

/**
 * Custom hook to consume the ThemeContext.
 *
 * @returns {ThemeContextType} The context value.
 * @throws {Error} If used outside of a ThemeProvider.
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/** Key used to persist the theme preference in localStorage. */
const STORAGE_KEY = "theme";

/** Narrows an arbitrary stored string to a valid `ThemeType`. */
const toTheme = (value: string | null): ThemeType =>
  themeList.includes(value as ThemeType) ? (value as ThemeType) : "system";

/** Reflects the resolved theme ("system" follows the OS) on the document. */
const applyThemeClass = (theme: ThemeType): void => {
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", isDark);
};

/**
 * Hook that owns the theme state: reads/persists the user's preference and
 * reflects it on the document, reacting to OS-level scheme changes while the
 * theme is "system". Meant to be called once, by `ThemeProvider`, and its
 * result passed into `ThemeContext.Provider` — components should consume the
 * context via `useTheme` instead of calling this hook directly.
 *
 * @returns {ThemeContextType} The current theme and a setter.
 */
export const useThemeState = (): ThemeContextType => {
  const [theme, setTheme] = useState<ThemeType>("system");

  // Runs client-side only (effects never run during SSR), so localStorage is
  // safe to read directly — no `typeof window` guard needed.
  useEffect(() => {
    setTheme(toTheme(localStorage.getItem(STORAGE_KEY)));
  }, []);

  useEffect(() => {
    applyThemeClass(theme);
    if (theme !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => applyThemeClass(theme);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [theme]);

  const changeTheme = useCallback((newTheme: ThemeType) => {
    setTheme(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, []);

  return useMemo(() => ({ theme, changeTheme }), [theme, changeTheme]);
};
